/**
 * 邮件列表面板组件 — 带分页和标签页切换的邮件列表。
 *
 * 主要导出：`EmailListPanel`（自定义元素 `<email-list-panel>`）
 *
 * 功能特性：
 * - 「全部邮件」标签页：分页展示当前文件夹/客户的所有邮件
 * - 「当前对话」标签页：展示当前选中邮件所属线程的所有消息
 * - 订阅 emailStore 变化，保持列表数据与全局状态同步
 * - 支持文件夹切换、客户筛选、已读/未读筛选
 * - 未读邮件排在列表前面，已读邮件标记为已读后即时更新
 * - 列表项点击后派发 `crm-email-select` 事件
 *
 * 依赖关系：
 * - 继承 `BaseElement`
 * - 通过 `emailApi` 和 `emailThreadApi` 获取邮件列表和线程消息
 * - 通过 `emailStore` 订阅全局邮件状态变化
 */
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Email } from '../types.js';
import { emailApi, emailThreadApi } from '../api.js';
import { emailStore } from '../store.js';
import { BaseElement } from '../base-element.js';

@customElement('email-list-panel')
export class EmailListPanel extends BaseElement {
  /** 当前选中的邮件 ID（用于高亮列表项） */
  @property({ type: Number }) selectedEmailId: number | null = null;
  /** 已读筛选：null 全部 / 0 未读 / 1 已读 */
  @property({ type: Number }) filterRead: number | null = null;
  /** 当前选中的邮件对象（用于加载线程消息） */
  @property({ type: Object }) currentEmail: Email | null = null;
  /** 当前文件夹名称（inbox / sent / drafts 等） */
  @property({ type: String }) currentFolder = 'inbox';
  /** 客户 ID 筛选（非 null 时只显示该客户的邮件） */
  @property({ type: Number }) customerId: number | null = null;

  /** 当前激活的标签页：'all' 全部邮件 / 'thread' 当前对话 */
  @state() private activeTab: 'all' | 'thread' = 'all';
  /** 全部邮件标签页的邮件列表 */
  @state() private panelEmails: Email[] = [];
  /** 对话标签页的线程消息列表 */
  @state() private threadEmails: Email[] = [];
  /** 全部邮件标签页的当前页码 */
  @state() private panelPage = 1;
  /** 全部邮件标签页的总记录数 */
  @state() private panelTotal = 0;
  /** 线程消息加载中状态 */
  @state() private isLoadingThread = false;

  /** 每页显示条数 */
  private perPage = 20;
  /** 全部邮件请求序号，仅允许最后一次分页或筛选请求更新面板。 */
  private _listRequestId = 0;
  /** 线程请求序号，仅允许最后一次选中邮件的请求更新面板。 */
  private _threadRequestId = 0;

  static styles = css`
    :host { display: flex; flex-direction: column; height: 100%; }
    .list-container { flex: 1; overflow-y: auto; min-height: 0; }
    .list-container::-webkit-scrollbar { width: 4px; }
    .list-container::-webkit-scrollbar-thumb { background: var(--color-border, #e0e1e6); border-radius: 2px; }
  `;

  /** emailStore 订阅取消函数 */
  private _unsubscribe?: () => void;

  /** 生命周期：连接 DOM 时订阅 emailStore 状态变化 */
  connectedCallback() {
    super.connectedCallback();
    // 订阅全局邮件状态，当 store 中的当前页码与面板页码一致时同步数据
    this._unsubscribe = emailStore.subscribe((s) => {
      if (s.currentPage === this.panelPage) {
        this.panelEmails = s.emails;
        this.panelTotal = s.total;
      }
    });
  }

  /** 生命周期：从 DOM 移除时取消订阅，防止内存泄漏 */
  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribe?.();
  }

  /**
   * 生命周期：属性变化后触发。
   * - currentFolder 变化时重置到第一页
   * - currentEmail 变化且当前在对话标签页时加载线程消息
   * - customerId 变化时重置到第一页
   * @param changedProps - 发生变化的属性 Map
   */
  updated(changedProps: Map<string, any>) {
    if (changedProps.has('currentFolder') && changedProps.get('currentFolder') !== undefined) {
      this.goToPage(1);
    }
    if (changedProps.has('currentEmail') && this.currentEmail && this.activeTab === 'thread') {
      this.loadThreadEmails();
    }
    if (changedProps.has('customerId') && this.customerId) {
      this.goToPage(1);
    }
  }

  /**
   * 切换标签页。
   * @param tab - 'all' 全部邮件 / 'thread' 当前对话
   */
  private switchTab(tab: 'all' | 'thread') {
    this.activeTab = tab;
    if (tab === 'thread' && this.currentEmail) {
      this.loadThreadEmails();
    }
  }

  /**
   * 加载当前选中邮件所属线程的所有消息。
   * @sideeffect 更新 threadEmails 和 isLoadingThread 状态
   */
  private async loadThreadEmails() {
    if (!this.currentEmail) return;
    const requestId = ++this._threadRequestId;
    this.isLoadingThread = true;
    try {
      const resp = await emailThreadApi.getEmailThread(this.currentEmail.id);
      if (requestId === this._threadRequestId) this.threadEmails = resp.messages;
    } catch { if (requestId === this._threadRequestId) this.threadEmails = []; }
    finally { if (requestId === this._threadRequestId) this.isLoadingThread = false; }
  }

  /**
   * 跳转到指定页码 — 调用 API 获取该页邮件列表。
   * @param page - 目标页码（从 1 开始）
   * @sideeffect 更新 panelEmails、panelPage、panelTotal 状态
   */
  private async goToPage(page: number) {
    const requestId = ++this._listRequestId;
    try {
      const resp = await emailApi.list({
        page, per_page: this.perPage,
        is_read: this.filterRead === null ? undefined : this.filterRead,
        folder: this.currentFolder || undefined,
        customer_id: this.customerId ?? undefined,
      });
      if (requestId !== this._listRequestId) return;
      this.panelEmails = resp.data;
      this.panelPage = page;
      this.panelTotal = resp.pagination.total;
    } catch { /* silent — 静默失败，避免列表页报错干扰用户 */ }
  }

  /**
   * 邮件选择处理 — 派发 `crm-email-select` 事件并即时标记为已读。
   * 乐观更新：无需等待后端返回，直接在列表中将未读邮件标记为已读。
   * @param email - 被选中的邮件对象
   * @sideeffect 派发事件，更新 panelEmails 中对应邮件的 is_read 状态
   */
  private handleSelect(email: Email) {
    this.dispatchEvent(new CustomEvent('crm-email-select', {
      detail: { emailId: email.id, email }, bubbles: true, composed: true,
    }));
    // 乐观更新：未读邮件点击后即时标记为已读
    if (email.is_read === 0) {
      this.panelEmails = this.panelEmails.map(e =>
        e.id === email.id ? { ...e, is_read: 1 } : e
      );
    }
  }

  /**
   * 格式化邮件日期显示。
   * 今天：显示时分（如 14:30）；其他日期：显示月日（如 1月5日）。
   * @param ts - Unix 时间戳（秒）
   * @returns 格式化后的日期字符串
   */
  private formatDate(ts: number | undefined): string {
    if (!ts) return '';
    const d = new Date(ts * 1000);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    return isToday ? d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
  }

  /**
   * 提取发件人显示名称 — 优先使用名称，无名称时使用邮箱。
   * @param email - 邮件对象
   * @returns 发件人显示字符串
   */
  private formatSender(email: Email): string {
    return email.send_from?.name || email.send_from?.email || '未知';
  }

  /**
   * 渲染单个邮件列表项。
   * @param email - 邮件对象
   * @returns Lit 模板，包含未读圆点、主题、发件人、日期
   */
  private renderItem(email: Email) {
    const isSelected = email.id === this.selectedEmailId;
    const isUnread = email.is_read === 0;
    return html`
      <div class="flex items-start gap-2 px-3 py-2.5 cursor-pointer transition-colors border-b border-border/50
                  ${isSelected ? 'bg-primary/10 border-l-[3px] border-l-primary' : 'border-l-[3px] border-l-transparent hover:bg-bg/60'}"
           @click=${() => this.handleSelect(email)}>
        <span class="mt-1.5 shrink-0">${isUnread
          ? html`<span class="inline-block w-2 h-2 rounded-full bg-info"></span>`
          : html`<span class="inline-block w-2 h-2"></span>`}</span>
        <div class="flex-1 min-w-0">
          <div class="text-sm truncate ${isUnread ? 'font-semibold text-text' : 'font-normal text-text-secondary'}">${email.subject || '无主题'}</div>
          <div class="flex justify-between items-center gap-2 mt-0.5">
            <span class="text-xs text-text-muted truncate">${this.formatSender(email)}</span>
            <span class="text-xs text-text-muted shrink-0">${this.formatDate(email.email_date || (email as any).created_at)}</span>
          </div>
        </div>
      </div>`;
  }

  /**
   * 渲染分页控件（仅全部邮件标签页显示）。
   * 总页数 ≤ 1 时不渲染。
   * @returns Lit 模板或空字符串
   */
  private renderPagination() {
    const totalPages = Math.ceil(this.panelTotal / this.perPage);
    if (totalPages <= 1) return '';

    return html`
      <div class="flex justify-center items-center gap-1.5 py-2 text-text-secondary text-xs border-t border-border flex-shrink-0">
        <button class="px-2 py-1.5 bg-surface hover:bg-bg border border-border text-text rounded disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                @click=${() => this.goToPage(this.panelPage - 1)} ?disabled=${this.panelPage <= 1}>上一页</button>
        <span class="px-1">${this.panelPage} / ${totalPages}</span>
        <button class="px-2 py-1.5 bg-surface hover:bg-bg border border-border text-text rounded disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                @click=${() => this.goToPage(this.panelPage + 1)} ?disabled=${this.panelPage >= totalPages}>下一页</button>
      </div>`;
  }

  /**
   * 渲染邮件列表面板。
   * 布局：标签页切换栏 → 邮件列表 → 分页控件（仅全部邮件标签页）。
   * 未读邮件排在前面（排序权重：未读 -1，已读 +1）。
   * @returns Lit 模板
   */
  render() {
    // 按未读优先排序
    const sorted = this.activeTab === 'all' ? [...this.panelEmails] : [...this.threadEmails];
    sorted.sort((a, b) => (a.is_read === 0 ? -1 : 1) - (b.is_read === 0 ? -1 : 1));
    const emails = sorted;
    const loading = this.activeTab === 'thread' && this.isLoadingThread;

    return html`
      <div class="flex flex-col h-full bg-surface">
        <div class="flex bg-bg rounded-md mx-3 mt-3 p-0.5 border border-border flex-shrink-0">
          <button class="flex-1 px-2 py-1.5 text-xs font-medium rounded transition-colors cursor-pointer
                         ${this.activeTab === 'all' ? 'bg-surface text-text shadow-sm' : 'text-text-muted hover:text-text'}"
                  @click=${() => this.switchTab('all')}>全部邮件</button>
          <button class="flex-1 px-2 py-1.5 text-xs font-medium rounded transition-colors cursor-pointer
                         ${this.activeTab === 'thread' ? 'bg-surface text-text shadow-sm' : 'text-text-muted hover:text-text'}"
                  @click=${() => this.switchTab('thread')}>当前对话</button>
        </div>
        <div class="list-container mt-2">
          ${loading ? html`<div class="text-center py-8 text-text-muted text-sm">加载中...</div>` : ''}
          ${!loading && emails.length === 0 ? html`
            <div class="text-center py-8 text-text-muted text-sm">
              ${this.activeTab === 'all' ? '暂无邮件' : '暂无对话邮件'}
            </div>` : ''}
          ${emails.map(e => this.renderItem(e))}
        </div>
        ${this.activeTab === 'all' ? this.renderPagination() : ''}
      </div>`;
  }
}
